/**
 * ProfileDrawer — W3W4 P2-a.
 *
 * Drawer BODY mounted at app root alongside <SettingsDrawer /> INSIDE the
 * <ProfileDrawerProvider> (App.tsx). Avatar tap on the lobby TopBar opens
 * this drawer (W1 Risk #5 placeholder now wired).
 *
 * Sections (per plan §P2-a):
 *   1. Account — Telegram first_name + photo_url + anonymous toggle inline
 *      (calls PATCH /v1/me/settings on toggle).
 *   2. Stats — totalAttempts, bestScore, weeklyBest, daysActive from /v1/me
 *      + /v1/me/daily/history.
 *   3. Data — "Delete my data" → confirmation modal → DELETE /v1/me →
 *      clear local state + navigate to landing (soft re-mount).
 *   4. Info — version (package.json + VITE_BUILD_SHA).
 */

import { useCallback, useEffect, useState, type ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Drawer } from './Drawer'
import { Switch } from './Switch'
import { Btn } from './Btn'
import { Avatar } from './Avatar'
import { useToast, useModal } from './useOverlay'
import { useProfileDrawer } from './useProfileDrawer'
import { useMe } from '../providers/MeProvider'
import { apiClient, ApiError } from '../api/client'
import { syncSettings } from '../lib/syncSettings'

// ── Section header ────────────────────────────────────────────────────────────

function SectionCap({ label }: { label: string }): ReactElement {
  return (
    <div
      className="cap"
      style={{
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontWeight: 600,
        marginTop: 12,
        marginBottom: 4,
      }}
    >
      {label}
    </div>
  )
}

function Row({
  label,
  sub,
  value,
}: {
  label: string
  sub?: string
  value?: ReactElement | string | number
}): ReactElement {
  return (
    <div className="set-row">
      <div>
        <div className="set-row__lbl">{label}</div>
        {sub !== undefined && <div className="set-row__sub">{sub}</div>}
      </div>
      {value !== undefined && (
        <div style={{ color: 'var(--tg-hint-color)' }}>{value}</div>
      )}
    </div>
  )
}

// ── ProfileDrawer body ────────────────────────────────────────────────────────

function ProfileBody({ onClose }: { onClose: () => void }): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { openModal } = useModal()
  const { me, refresh } = useMe()

  const [anonymous, setAnonymous] = useState<boolean>(false)
  const [totalAttempts, setTotalAttempts] = useState<number | null>(null)
  const [bestScore, setBestScore] = useState<number | null>(null)
  const [weeklyBest, setWeeklyBest] = useState<number | null>(null)
  const [daysActive, setDaysActive] = useState<number | null>(null)

  // Seed local state from server snapshot.
  useEffect(() => {
    if (me) setAnonymous(me.user.anonymousMode)
  }, [me])

  // Pull stats from daily-history endpoint (smallest existing surface).
  useEffect(() => {
    let cancelled = false
    void apiClient
      .getDailyHistory({ limit: 50, offset: 0 })
      .then((resp) => {
        if (cancelled) return
        const items = resp.items
        const total = resp.total
        setTotalAttempts(total)
        if (items.length === 0) {
          setBestScore(0)
          setWeeklyBest(0)
          setDaysActive(0)
          return
        }
        let best = 0
        const dayKeys = new Set<string>()
        for (const it of items) {
          if (it.score > best) best = it.score
          dayKeys.add(it.date)
        }
        setBestScore(best)
        setDaysActive(dayKeys.size)
        // weekly best = highest score within last 7 distinct dates by submittedAt
        const now = Date.now()
        const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000
        let weekly = 0
        for (const it of items) {
          if (new Date(it.submittedAt).getTime() >= sevenDaysAgo && it.score > weekly) {
            weekly = it.score
          }
        }
        setWeeklyBest(weekly)
      })
      .catch(() => {
        if (cancelled) return
        // Non-critical — leave nulls; UI renders skeleton dashes.
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Anonymous toggle — optimistic local + PATCH via syncSettings.
  const handleAnonymousToggle = useCallback(
    (next: boolean): void => {
      if (next === anonymous) return
      setAnonymous(next)
      void syncSettings({ anonymousMode: next })
      void refresh()
    },
    [anonymous, refresh],
  )

  const handleDelete = useCallback((): void => {
    const close = openModal({
      title: t('profile.section.data.deleteConfirmTitle'),
      children: (
        <div>
          <p style={{ marginBottom: 12 }}>{t('profile.section.data.deleteConfirmBody')}</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => close()}>
              {t('profile.section.data.deleteCancel')}
            </Btn>
            <Btn
              variant="danger"
              onClick={async () => {
                close()
                try {
                  await apiClient.deleteMe()
                  // Clear local state. localStorage settings persist (will
                  // re-sync on next /v1/me); attempts/leaderboard rows are
                  // deleted server-side via cascade.
                  showToast('success', t('profile.section.data.deleted'))
                  onClose()
                  // Soft re-mount to landing — forces /v1/me 404 → lazy upsert.
                  navigate('/', { replace: true })
                } catch (err) {
                  const msg = err instanceof ApiError ? `${err.status}` : 'error'
                  showToast('error', `Delete failed: ${msg}`)
                }
              }}
            >
              {t('profile.section.data.deleteConfirm')}
            </Btn>
          </div>
        </div>
      ),
    })
  }, [navigate, onClose, openModal, showToast, t])

  // ── Section 1: Account ────────────────────────────────────────────────────
  const tgUser = typeof window !== 'undefined' ? window.Telegram?.WebApp?.initDataUnsafe?.user : undefined
  const displayName = me?.user.firstName ?? tgUser?.first_name ?? '—'
  const photoUrl = tgUser?.photo_url

  // ── Section 4: Info ───────────────────────────────────────────────────────
  const buildSha = (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_BUILD_SHA ?? 'dev'

  return (
    <div className="settings-body">
      <SectionCap label={t('profile.section.account.title')} />
      <div className="set-row" style={{ alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar name={displayName} size={48} photo_url={photoUrl} />
          <div>
            <div className="set-row__lbl">{displayName}</div>
            {tgUser?.username !== undefined && (
              <div className="set-row__sub">@{tgUser.username}</div>
            )}
          </div>
        </div>
      </div>
      <div className="set-row">
        <div>
          <div className="set-row__lbl">{t('profile.section.account.anonymous')}</div>
          <div className="set-row__sub">{t('profile.section.account.anonymousSub')}</div>
        </div>
        <Switch
          on={anonymous}
          onChange={handleAnonymousToggle}
          aria-label={t('profile.section.account.anonymous')}
        />
      </div>

      <SectionCap label={t('profile.section.stats.title')} />
      <Row label={t('profile.section.stats.totalAttempts')} value={totalAttempts ?? '—'} />
      <Row label={t('profile.section.stats.bestScore')} value={bestScore ?? '—'} />
      <Row label={t('profile.section.stats.weeklyBest')} value={weeklyBest ?? '—'} />
      <Row label={t('profile.section.stats.daysActive')} value={daysActive ?? '—'} />

      <SectionCap label={t('profile.section.data.title')} />
      <button
        type="button"
        className="set-row set-row--btn"
        onClick={handleDelete}
        style={{ background: 'none', border: 'none', width: '100%', padding: 0, cursor: 'pointer' }}
      >
        <div>
          <div className="set-row__lbl" style={{ color: 'var(--danger)' }}>
            {t('profile.section.data.delete')}
          </div>
          <div className="set-row__sub">{t('profile.section.data.deleteSub')}</div>
        </div>
      </button>

      <SectionCap label={t('profile.section.info.title')} />
      <Row label={t('profile.section.info.version')} value={`v0.0.1 (${buildSha})`} />
    </div>
  )
}

export function ProfileDrawer(): ReactElement {
  const { t } = useTranslation()
  const ctx = useProfileDrawer()

  return (
    <Drawer open={ctx.isOpen} onClose={ctx.close} title={t('profile.title')}>
      <ProfileBody onClose={ctx.close} />
    </Drawer>
  )
}
